/**
 * presetTemplates.ts
 * Generates ready-to-use DOCX templates in memory for the 7 most common
 * end-user document types, each with a clean BizBox.Asia text-brand header
 * (no image file needed — prints/PDFs identically) and a full set of
 * `{{field}}` markers + `{{#items}}…{{/items}}` detail loop + `⟦SUM:…⟧` totals.
 *
 * The builder is shared: each preset is just a config (title, metadata rows,
 * detail columns, signature roles). This keeps the 7 templates consistent and
 * cheap to maintain.
 */

import PizZip from "pizzip";
import type { FieldFormattingConfig } from "@/utils/formatter";

/* ------------------------------------------------------------------ */
/*  Shared DOCX package parts (same for every preset)                  */
/* ------------------------------------------------------------------ */

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>
  <Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>
  <Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>
</Types>`;

const ROOT_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

const DOC_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/>
  <Relationship Id="rId4" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>
</Relationships>`;

const SETTINGS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:defaultTabStop w:val="720"/>
  <w:compat/>
</w:settings>`;

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>
        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
        <w:sz w:val="22"/>
        <w:szCs w:val="22"/>
        <w:lang w:val="vi-VN"/>
      </w:rPr>
    </w:rPrDefault>
    <w:pPrDefault>
      <w:pPr>
        <w:spacing w:after="100" w:line="260" w:lineRule="auto"/>
      </w:pPr>
    </w:pPrDefault>
  </w:docDefaults>
</w:styles>`;

/* ------------------------------------------------------------------ */
/*  Config types                                                       */
/* ------------------------------------------------------------------ */

export interface PresetMetaRow {
  /** Bold label, e.g. "Mã đơn hàng / PO:". */
  label: string;
  /** {{field}} marker, e.g. "{{Mã đơn hàng}}". */
  field: string;
  /** When true, this cell spans both columns of the 2-col metadata table. */
  fullWidth?: boolean;
}

export interface PresetDetailColumn {
  /** Header text, e.g. "STT". */
  header: string;
  /** {{field}} marker in the repeating row, e.g. "{{STT}}". */
  field: string;
  /** Column width in twips (dxa). */
  width: number;
  /** Alignment of the cell content. */
  align?: "left" | "center" | "right";
  /** When true, this column is summed in the total row via ⟦SUM:field⟧. */
  sum?: boolean;
}

export interface PresetSignatureRole {
  /** Bold role label, e.g. "NGƯỜI LẬP PHIẾU". */
  label: string;
}

export interface PresetTemplateConfig {
  /** Stable id (used as the picker key). */
  id: string;
  /** Human name shown in the picker, e.g. "Phiếu giao hàng". */
  name: string;
  /** Short description for the picker card. */
  description: string;
  /** File name used when downloading. */
  fileName: string;
  /** Big centered document title, e.g. "PHIẾU GIAO HÀNG". */
  title: string;
  /** Optional subtitle under the title. */
  subtitle?: string;
  /** Metadata rows (2-col borderless table). */
  metaRows: PresetMetaRow[];
  /** Detail table columns. */
  detailColumns: PresetDetailColumn[];
  /** Signature roles (3-col borderless table). */
  signatureRoles: PresetSignatureRole[];
}

/* ------------------------------------------------------------------ */
/*  XML helpers                                                        */
/* ------------------------------------------------------------------ */

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Build the metadata table XML from a flat list of rows (paired 2-up). */
// 4 fixed columns [label | value] x2 so labels always line up across rows
// (auto-width tables shrink to content and drift as label lengths differ).
function metaTableXml(rows: PresetMetaRow[]): string {
  const COL_LABEL = 2000; // twips — fixed label column width
  const COL_VALUE = 2950; // twips — fixed value column width
  const GRID = `<w:gridCol w:w="${COL_LABEL}"/><w:gridCol w:w="${COL_VALUE}"/><w:gridCol w:w="${COL_LABEL}"/><w:gridCol w:w="${COL_VALUE}"/>`;

  const labelCell = (label: string) =>
    `<w:tc><w:tcPr><w:vAlign w:val="top"/></w:tcPr>
      <w:p><w:pPr><w:spacing w:after="40"/><w:jc w:val="right"/></w:pPr>
        <w:r><w:rPr><w:b/></w:rPr><w:t>${esc(label)}</w:t></w:r>
      </w:p>
    </w:tc>`;

  const valueCell = (field: string, span = 1) =>
    `<w:tc>${span > 1 ? `<w:tcPr><w:gridSpan w:val="${span}"/></w:tcPr>` : ""}
      <w:p><w:pPr><w:spacing w:after="40"/><w:jc w:val="left"/></w:pPr>
        <w:r><w:t>${esc(field)}</w:t></w:r>
      </w:p>
    </w:tc>`;

  const rowsXml: string[] = [];
  let i = 0;
  while (i < rows.length) {
    const r = rows[i];
    if (r.fullWidth) {
      // Label cell + value cell spanning the remaining 3 columns.
      rowsXml.push(`<w:tr>${labelCell(r.label)}${valueCell(r.field, 3)}</w:tr>`);
      i += 1;
    } else {
      const a = rows[i];
      const b = rows[i + 1];
      if (b) {
        rowsXml.push(`<w:tr>${labelCell(a.label)}${valueCell(a.field)}${labelCell(b.label)}${valueCell(b.field)}</w:tr>`);
        i += 2;
      } else {
        // Odd last row: value cell spans the last 3 columns.
        rowsXml.push(`<w:tr>${labelCell(a.label)}${valueCell(a.field, 3)}</w:tr>`);
        i += 1;
      }
    }
  }

  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="9900" w:type="dxa"/>
      <w:tblLayout w:val="fixed"/>
      <w:tblBorders>
        <w:top w:val="none"/><w:left w:val="none"/><w:bottom w:val="none"/><w:right w:val="none"/>
        <w:insideH w:val="none"/><w:insideV w:val="none"/>
      </w:tblBorders>
      <w:tblCellMar>
        <w:top w:w="60" w:type="dxa"/><w:bottom w:w="60" w:type="dxa"/>
        <w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
    <w:tblGrid>${GRID}</w:tblGrid>
    ${rowsXml.join("\n")}
  </w:tbl>`;
}

/** Build the bordered detail table: header row + repeating data row + total row. */
function detailTableXml(cols: PresetDetailColumn[]): string {
  const grid = cols.map((c) => `<w:gridCol w:w="${c.width}"/>`).join("");
  const headerCells = cols
    .map(
      (c) =>
        `<w:tc><w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="1F4E79"/><w:vAlign w:val="center"/></w:tcPr>
          <w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr>
            <w:r><w:rPr><w:b/><w:sz w:val="20"/><w:color w:val="FFFFFF"/></w:rPr><w:t>${esc(c.header)}</w:t></w:r>
          </w:p>
        </w:tc>`
    )
    .join("");

  const dataCells = cols
    .map((c, idx) => {
      const align = c.align ?? "center";
      const open = idx === 0 ? "{{#items}}" : "";
      const close = idx === cols.length - 1 ? "{{/items}}" : "";
      return `<w:tc><w:tcPr><w:vAlign w:val="center"/></w:tcPr>
        <w:p><w:pPr><w:jc w:val="${align}"/><w:spacing w:after="0"/></w:pPr>
          <w:r><w:t>${open}</w:t></w:r>
          <w:r><w:rPr><w:sz w:val="20"/></w:rPr><w:t>${esc(c.field)}</w:t></w:r>
          <w:r><w:t>${close}</w:t></w:r>
        </w:p>
      </w:tc>`;
    })
    .join("");

  // Total row: first cell spans all but the summed columns; each summed column
  // gets a ⟦SUM:field⟧ cell.
  const sumCols = cols.filter((c) => c.sum);
  const nonSumCount = cols.length - sumCols.length;
  const totalCells: string[] = [];
  if (nonSumCount > 0) {
    totalCells.push(
      `<w:tc><w:tcPr><w:gridSpan w:val="${nonSumCount}"/><w:vAlign w:val="center"/></w:tcPr>
        <w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr>
          <w:r><w:rPr><w:b/><w:sz w:val="20"/></w:rPr><w:t>TỔNG CỘNG</w:t></w:r>
        </w:p>
      </w:tc>`
    );
  }
  for (const c of sumCols) {
    totalCells.push(
      `<w:tc><w:tcPr><w:vAlign w:val="center"/></w:tcPr>
        <w:p><w:pPr><w:jc w:val="right"/><w:spacing w:after="0"/></w:pPr>
          <w:r><w:rPr><w:b/><w:sz w:val="20"/></w:rPr><w:t>{{SUM:${esc(c.field.replace(/[{}]/g, ""))}}}</w:t></w:r>
        </w:p>
      </w:tc>`
    );
  }

  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="${cols.reduce((s, c) => s + c.width, 0)}" w:type="dxa"/>
      <w:tblLayout w:val="fixed"/>
      <w:tblBorders>
        <w:top w:val="single" w:sz="8" w:space="0" w:color="000000"/>
        <w:left w:val="single" w:sz="8" w:space="0" w:color="000000"/>
        <w:bottom w:val="single" w:sz="8" w:space="0" w:color="000000"/>
        <w:right w:val="single" w:sz="8" w:space="0" w:color="000000"/>
        <w:insideH w:val="single" w:sz="6" w:space="0" w:color="000000"/>
        <w:insideV w:val="single" w:sz="6" w:space="0" w:color="000000"/>
      </w:tblBorders>
      <w:tblCellMar>
        <w:top w:w="90" w:type="dxa"/><w:bottom w:w="90" w:type="dxa"/>
        <w:left w:w="100" w:type="dxa"/><w:right w:w="100" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
    <w:tblGrid>${grid}</w:tblGrid>
    <w:tr><w:trPr><w:tblHeader/><w:cantSplit/></w:trPr>${headerCells}</w:tr>
    <w:tr><w:trPr><w:cantSplit/></w:trPr>${dataCells}</w:tr>
    <w:tr><w:trPr><w:cantSplit/></w:trPr>${totalCells.join("")}</w:tr>
  </w:tbl>`;
}

/**
 * Build a right-aligned "Số tiền bằng chữ: {{TEXT:SUM:…}} đồng" line under the
 * detail table — for accounting / invoices. Picks the currency column: prefers
 * one named "Thành tiền" / "Tổng tiền", else the last summed column.
 */
function amountInWordsRowXml(cols: PresetDetailColumn[]): string {
  const summed = cols.filter((c) => c.sum && c.field);
  if (summed.length === 0) return "";
  const preferred = summed.find((c) => /thành tiền|tổng tiền|tổng cộng/i.test(c.field.replace(/[{}]/g, "")));
  const pick = preferred ?? summed[summed.length - 1];
  const fieldName = pick.field.replace(/[{}]/g, "");
  return `<w:p><w:pPr><w:jc w:val="right"/><w:spacing w:before="120" w:after="0"/></w:pPr>
    <w:r><w:rPr><w:sz w:val="20"/></w:rPr><w:t>Số tiền bằng chữ: </w:t></w:r>
    <w:r><w:rPr><w:b/><w:sz w:val="20"/></w:rPr><w:t>{{TEXT:SUM:${esc(fieldName)}}}</w:t></w:r>
    <w:r><w:rPr><w:sz w:val="20"/></w:rPr><w:t> đồng</w:t></w:r>
  </w:p>`;
}

/** Build the 3-column signature table. */
function signatureTableXml(roles: PresetSignatureRole[]): string {
  const cells = roles
    .map(
      (r) =>
        `<w:tc>
          <w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="40"/></w:pPr>
            <w:r><w:rPr><w:b/><w:sz w:val="21"/></w:rPr><w:t>${esc(r.label)}</w:t></w:r>
          </w:p>
          <w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="800"/></w:pPr>
            <w:r><w:rPr><w:i/><w:sz w:val="18"/></w:rPr><w:t>(Ký và ghi rõ họ tên)</w:t></w:r>
          </w:p>
        </w:tc>`
    )
    .join("");
  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="0" w:type="auto"/>
      <w:tblBorders>
        <w:top w:val="none"/><w:left w:val="none"/><w:bottom w:val="none"/><w:right w:val="none"/>
        <w:insideH w:val="none"/><w:insideV w:val="none"/>
      </w:tblBorders>
      <w:tblCellMar>
        <w:top w:w="60" w:type="dxa"/><w:bottom w:w="60" w:type="dxa"/>
        <w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
    <w:tblGrid><w:gridCol w:w="3300"/><w:gridCol w:w="3300"/><w:gridCol w:w="3300"/></w:tblGrid>
    <w:tr>${cells}</w:tr>
  </w:tbl>`;
}

/**
 * Default display formats for numeric detail columns, keyed by normalized
 * field name. Presets apply these when the template is loaded so money /
 * quantities already render formatted (1.234,56 / ₫ / %) without the user
 * having to pick a format in the editor.
 */
export function defaultFieldFormats(
  columns: PresetDetailColumn[]
): Record<string, FieldFormattingConfig> {
  const out: Record<string, FieldFormattingConfig> = {};
  const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  for (const c of columns) {
    const n = norm(c.field.replace(/[{}]/g, ""));
    if (/thanhtien|tongtien|dongia|thanhgia|gia|tien|giatri/.test(n)) {
      out[c.field] = { formatType: "currency", decimalPlaces: 2, currencySymbol: "₫" };
    } else if (/soluong|khoiluong|sokg|trongluong/.test(n)) {
      out[c.field] = { formatType: "number", decimalPlaces: 2 };
    } else if (/phantram|tyle|tile/.test(n)) {
      out[c.field] = { formatType: "percent", decimalPlaces: 1 };
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Document builder                                                   */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Header / footer parts (repeat on EVERY page)                       */
/* ------------------------------------------------------------------ */

/** Enterprise running header — repeats on every page via the sectPr ref. */
function headerXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:tbl>
    <w:tblPr>
      <w:tblW w:w="0" w:type="auto"/>
      <w:tblBorders>
        <w:top w:val="none"/><w:left w:val="none"/><w:bottom w:val="single" w:sz="12" w:space="0" w:color="1F4E79"/>
        <w:right w:val="none"/><w:insideH w:val="none"/><w:insideV w:val="none"/>
      </w:tblBorders>
      <w:tblCellMar>
        <w:top w:w="60" w:type="dxa"/><w:bottom w:w="120" w:type="dxa"/>
        <w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
    <w:tblGrid><w:gridCol w:w="5000"/><w:gridCol w:w="4900"/></w:tblGrid>
    <w:tr>
      <w:tc>
        <w:p><w:pPr><w:spacing w:after="20" w:line="240" w:lineRule="auto"/></w:pPr>
          <w:r><w:rPr><w:b/><w:sz w:val="34"/><w:szCs w:val="34"/><w:color w:val="1F4E79"/></w:rPr><w:t>BizBox.Asia</w:t></w:r>
        </w:p>
        <w:p><w:pPr><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr>
          <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t>Giải pháp quản trị doanh nghiệp toàn diện · ERP &amp; In ấn chứng từ chuyên nghiệp</w:t></w:r>
        </w:p>
      </w:tc>
      <w:tc>
        <w:p><w:pPr><w:jc w:val="right"/><w:spacing w:after="20" w:line="240" w:lineRule="auto"/></w:pPr>
          <w:r><w:rPr><w:b/><w:sz w:val="20"/><w:szCs w:val="20"/></w:rPr><w:t>CÔNG TY CỔ PHẦN BIZBOX ASIA</w:t></w:r>
        </w:p>
        <w:p><w:pPr><w:jc w:val="right"/><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr>
          <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t>Lầu 8, Tòa nhà Helium, 123 Nguyễn Đình Chiểu, P.6, Q.3, TP.HCM</w:t></w:r>
        </w:p>
        <w:p><w:pPr><w:jc w:val="right"/><w:spacing w:after="0" w:line="240" w:lineRule="auto"/></w:pPr>
          <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t>MST: 0312345678 · Tel: 028.7300.1234 · www.bizbox.asia</w:t></w:r>
        </w:p>
      </w:tc>
    </w:tr>
  </w:tbl>
</w:hdr>`;
}

/** Enterprise running footer — repeats on every page. */
function footerXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:p>
    <w:pPr>
      <w:jc w:val="center"/>
      <w:spacing w:before="60"/>
      <w:pBdr>
        <w:top w:val="single" w:sz="6" w:space="4" w:color="B7C4D6"/>
      </w:pBdr>
    </w:pPr>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t>Trang </w:t></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:fldChar w:fldCharType="begin"/></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:instrText xml:space="preserve"> PAGE </w:instrText></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:fldChar w:fldCharType="separate"/></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:t>1</w:t></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:fldChar w:fldCharType="end"/></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t> / </w:t></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t>Trang cuối: </w:t></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:fldChar w:fldCharType="begin"/></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:instrText xml:space="preserve"> NUMPAGES </w:instrText></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:fldChar w:fldCharType="separate"/></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:t>1</w:t></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="1F4E79"/></w:rPr><w:fldChar w:fldCharType="end"/></w:r>
    <w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="595959"/></w:rPr><w:t> · BizBox.Asia — Giải pháp quản trị doanh nghiệp</w:t></w:r>
  </w:p>
</w:ftr>`;
}

/** Section heading helper: small all-caps label with a left accent bar. */
function sectionHeadingXml(text: string): string {
  return `<w:p><w:pPr><w:spacing w:before="160" w:after="80"/></w:pPr>
    <w:r><w:rPr><w:b/><w:sz w:val="21"/><w:szCs w:val="21"/><w:color w:val="1F4E79"/></w:rPr><w:t>${esc(text)}</w:t></w:r>
  </w:p>`;
}

function buildDocumentXml(cfg: PresetTemplateConfig): string {
  const subtitle = cfg.subtitle
    ? `<w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="200"/></w:pPr>
        <w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t>${esc(cfg.subtitle)}</w:t></w:r>
      </w:p>`
    : "";

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
            xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:body>
    <!-- Title -->
    <w:p>
      <w:pPr><w:jc w:val="center"/><w:spacing w:before="200" w:after="120"/></w:pPr>
      <w:r><w:rPr><w:b/><w:sz w:val="34"/><w:szCs w:val="34"/><w:color w:val="1F4E79"/></w:rPr><w:t>${esc(cfg.title)}</w:t></w:r>
    </w:p>
    ${subtitle}

    <!-- Content section: general info -->
    ${sectionHeadingXml("THÔNG TIN CHUNG")}

    <!-- Metadata -->
    ${metaTableXml(cfg.metaRows)}

    <!-- Space -->
    <w:p><w:pPr><w:spacing w:before="120" w:after="120"/></w:pPr></w:p>

    <!-- Content section: detail -->
    ${sectionHeadingXml("CHI TIẾT")}

    <!-- Detail table -->
    ${detailTableXml(cfg.detailColumns)}

    <!-- Amount in words (accounting / invoices): Số tiền bằng chữ -->
    ${amountInWordsRowXml(cfg.detailColumns)}

    <!-- Space -->
    <w:p><w:pPr><w:spacing w:before="240" w:after="120"/></w:pPr></w:p>

    <!-- Signatures -->
    ${signatureTableXml(cfg.signatureRoles)}

    <!-- Page geometry: A4 portrait, with running header/footer -->
    <w:sectPr>
      <w:headerReference w:type="default" r:id="rId3"/>
      <w:footerReference w:type="default" r:id="rId4"/>
      <w:pgSz w:w="11906" w:h="16838" w:orient="portrait"/>
      <w:pgMar w:top="1250" w:right="1000" w:bottom="1250" w:left="1000" w:header="850" w:footer="850" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;
}

/** Assemble a complete .docx binary from a preset config. */
export function buildPresetDocx(cfg: PresetTemplateConfig): Uint8Array {
  const zip = new PizZip();
  zip.file("[Content_Types].xml", CONTENT_TYPES_XML);
  zip.file("_rels/.rels", ROOT_RELS_XML);
  zip.file("word/_rels/document.xml.rels", DOC_RELS_XML);
  zip.file("word/settings.xml", SETTINGS_XML);
  zip.file("word/styles.xml", STYLES_XML);
  zip.file("word/header1.xml", headerXml());
  zip.file("word/footer1.xml", footerXml());
  zip.file("word/document.xml", buildDocumentXml(cfg));
  return zip.generate({
    type: "uint8array",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

/* ------------------------------------------------------------------ */
/*  The 7 presets                                                      */
/* ------------------------------------------------------------------ */

export const PRESET_TEMPLATES: PresetTemplateConfig[] = [
  {
    id: "phieu-giao-hang",
    name: "Phiếu giao hàng",
    description: "Chứng từ giao hàng chuẩn kèm bảng kê sản phẩm, tổng hợp số lượng và khối lượng.",
    fileName: "Phieu_Giao_Hang_BizBox.docx",
    title: "PHIẾU GIAO HÀNG",
    subtitle: "Kèm bảng kê sản phẩm",
    metaRows: [
      { label: "Mã đơn hàng / PO: ", field: "{{Mã đơn hàng}}" },
      { label: "Ngày giao: ", field: "{{Ngày giao}}" },
      { label: "Khách hàng: ", field: "{{Khách hàng}}" },
      { label: "Điểm giao: ", field: "{{Điểm giao}}" },
      { label: "Địa chỉ giao: ", field: "{{Địa chỉ giao}}", fullWidth: true },
      { label: "Người nhận: ", field: "{{Người nhận}}" },
      { label: "Số điện thoại: ", field: "{{Số điện thoại}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Mã hàng", field: "{{Mã hàng}}", width: 1300, align: "center" },
      { header: "Tên hàng", field: "{{Tên hàng}}", width: 3400, align: "left" },
      { header: "ĐVT", field: "{{ĐVT}}", width: 800, align: "center" },
      { header: "Số lượng", field: "{{Số lượng}}", width: 1100, align: "right", sum: true },
      { header: "Khối lượng", field: "{{Khối lượng}}", width: 1100, align: "right", sum: true },
      { header: "Ghi chú", field: "{{Ghi chú}}", width: 1600, align: "left" },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP PHIẾU" },
      { label: "NGƯỜI GIAO HÀNG" },
      { label: "NGƯỜI NHẬN HÀNG" },
    ],
  },
  {
    id: "bao-gia",
    name: "Báo giá",
    description: "Bảng báo giá sản phẩm / dịch vụ với đơn giá, thành tiền và tổng cộng.",
    fileName: "Bao_Gia_BizBox.docx",
    title: "BÁO GIÁ",
    subtitle: "Bảng báo giá sản phẩm / dịch vụ",
    metaRows: [
      { label: "Số báo giá: ", field: "{{Số báo giá}}" },
      { label: "Ngày lập: ", field: "{{Ngày lập}}" },
      { label: "Khách hàng: ", field: "{{Khách hàng}}" },
      { label: "Hiệu lực đến: ", field: "{{Hiệu lực đến}}" },
      { label: "Địa chỉ: ", field: "{{Địa chỉ}}", fullWidth: true },
      { label: "Người liên hệ: ", field: "{{Người liên hệ}}" },
      { label: "Điện thoại: ", field: "{{Điện thoại}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Mã hàng", field: "{{Mã hàng}}", width: 1200, align: "center" },
      { header: "Tên hàng / Dịch vụ", field: "{{Tên hàng}}", width: 3000, align: "left" },
      { header: "ĐVT", field: "{{ĐVT}}", width: 800, align: "center" },
      { header: "Số lượng", field: "{{Số lượng}}", width: 1000, align: "right", sum: true },
      { header: "Đơn giá", field: "{{Đơn giá}}", width: 1200, align: "right" },
      { header: "Thành tiền", field: "{{Thành tiền}}", width: 1400, align: "right", sum: true },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP BÁO GIÁ" },
      { label: "DUYỆT GIÁ" },
      { label: "KHÁCH HÀNG" },
    ],
  },
  {
    id: "hoa-don",
    name: "Hoá đơn",
    description: "Hoá đơn bán hàng chuẩn với thuế GTGT, tổng tiền và chữ ký.",
    fileName: "Hoa_Don_BizBox.docx",
    title: "HOÁ ĐƠN BÁN HÀNG",
    subtitle: "Hóa đơn GTGT",
    metaRows: [
      { label: "Số hoá đơn: ", field: "{{Số hoá đơn}}" },
      { label: "Ngày lập: ", field: "{{Ngày lập}}" },
      { label: "Khách hàng: ", field: "{{Khách hàng}}" },
      { label: "Mã số thuế: ", field: "{{Mã số thuế}}" },
      { label: "Địa chỉ: ", field: "{{Địa chỉ}}", fullWidth: true },
      { label: "Hình thức thanh toán: ", field: "{{Hình thức thanh toán}}" },
      { label: "Người nhận: ", field: "{{Người nhận}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Mã hàng", field: "{{Mã hàng}}", width: 1200, align: "center" },
      { header: "Tên hàng / Dịch vụ", field: "{{Tên hàng}}", width: 3000, align: "left" },
      { header: "ĐVT", field: "{{ĐVT}}", width: 800, align: "center" },
      { header: "Số lượng", field: "{{Số lượng}}", width: 1000, align: "right", sum: true },
      { header: "Đơn giá", field: "{{Đơn giá}}", width: 1200, align: "right" },
      { header: "Thành tiền", field: "{{Thành tiền}}", width: 1400, align: "right", sum: true },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP HOÁ ĐƠN" },
      { label: "KẾ TOÁN TRƯỞNG" },
      { label: "KHÁCH HÀNG" },
    ],
  },
  {
    id: "phieu-xuat-kho",
    name: "Phiếu xuất kho",
    description: "Phiếu xuất kho nguyên vật liệu / thành phẩm với số lượng và kho.",
    fileName: "Phieu_Xuat_Kho_BizBox.docx",
    title: "PHIẾU XUẤT KHO",
    subtitle: "Xuất kho nguyên vật liệu / thành phẩm",
    metaRows: [
      { label: "Số phiếu: ", field: "{{Số phiếu}}" },
      { label: "Ngày xuất: ", field: "{{Ngày xuất}}" },
      { label: "Người nhận: ", field: "{{Người nhận}}" },
      { label: "Kho xuất: ", field: "{{Kho xuất}}" },
      { label: "Lý do xuất: ", field: "{{Lý do xuất}}", fullWidth: true },
      { label: "Đơn vị nhận: ", field: "{{Đơn vị nhận}}" },
      { label: "Địa chỉ: ", field: "{{Địa chỉ}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Mã hàng", field: "{{Mã hàng}}", width: 1300, align: "center" },
      { header: "Tên hàng", field: "{{Tên hàng}}", width: 3200, align: "left" },
      { header: "ĐVT", field: "{{ĐVT}}", width: 800, align: "center" },
      { header: "Số lượng", field: "{{Số lượng}}", width: 1100, align: "right", sum: true },
      { header: "Đơn giá", field: "{{Đơn giá}}", width: 1200, align: "right" },
      { header: "Thành tiền", field: "{{Thành tiền}}", width: 1400, align: "right", sum: true },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP PHIẾU" },
      { label: "THỦ KHO" },
      { label: "NGƯỜI NHẬN" },
    ],
  },
  {
    id: "phieu-luong",
    name: "Phiếu lương",
    description: "Phiếu lương nhân viên với các khoản thu nhập, khấu trừ và thực nhận.",
    fileName: "Phieu_Luong_BizBox.docx",
    title: "PHIẾU LƯƠNG",
    subtitle: "Bảng lương nhân viên",
    metaRows: [
      { label: "Mã nhân viên: ", field: "{{Mã nhân viên}}" },
      { label: "Kỳ lương: ", field: "{{Kỳ lương}}" },
      { label: "Họ tên: ", field: "{{Họ tên}}" },
      { label: "Phòng ban: ", field: "{{Phòng ban}}" },
      { label: "Chức danh: ", field: "{{Chức danh}}" },
      { label: "Ngày lập: ", field: "{{Ngày lập}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Khoản mục", field: "{{Khoản mục}}", width: 3600, align: "left" },
      { header: "Loại", field: "{{Loại}}", width: 1200, align: "center" },
      { header: "Số ngày / Số giờ", field: "{{Số ngày}}", width: 1600, align: "right" },
      { header: "Số tiền", field: "{{Số tiền}}", width: 1800, align: "right", sum: true },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP" },
      { label: "KẾ TOÁN" },
      { label: "NGƯỜI NHẬN LƯƠNG" },
    ],
  },
  {
    id: "phieu-nhap-kho",
    name: "Phiếu nhập kho",
    description: "Phiếu nhập kho hàng hoá / nguyên vật liệu với số lượng và kho.",
    fileName: "Phieu_Nhap_Kho_BizBox.docx",
    title: "PHIẾU NHẬP KHO",
    subtitle: "Nhập kho hàng hoá / nguyên vật liệu",
    metaRows: [
      { label: "Số phiếu: ", field: "{{Số phiếu}}" },
      { label: "Ngày nhập: ", field: "{{Ngày nhập}}" },
      { label: "Nhà cung cấp: ", field: "{{Nhà cung cấp}}" },
      { label: "Kho nhập: ", field: "{{Kho nhập}}" },
      { label: "Số hoá đơn: ", field: "{{Số hoá đơn}}" },
      { label: "Người giao: ", field: "{{Người giao}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Mã hàng", field: "{{Mã hàng}}", width: 1300, align: "center" },
      { header: "Tên hàng", field: "{{Tên hàng}}", width: 3200, align: "left" },
      { header: "ĐVT", field: "{{ĐVT}}", width: 800, align: "center" },
      { header: "Số lượng", field: "{{Số lượng}}", width: 1100, align: "right", sum: true },
      { header: "Đơn giá", field: "{{Đơn giá}}", width: 1200, align: "right" },
      { header: "Thành tiền", field: "{{Thành tiền}}", width: 1400, align: "right", sum: true },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP PHIẾU" },
      { label: "THỦ KHO" },
      { label: "NGƯỜI GIAO HÀNG" },
    ],
  },
  {
    id: "phieu-tra-hang",
    name: "Phiếu trả hàng",
    description: "Phiếu trả hàng cho nhà cung cấp / khách hàng với lý do trả.",
    fileName: "Phieu_Tra_Hang_BizBox.docx",
    title: "PHIẾU TRẢ HÀNG",
    subtitle: "Trả hàng cho nhà cung cấp / khách hàng",
    metaRows: [
      { label: "Số phiếu: ", field: "{{Số phiếu}}" },
      { label: "Ngày trả: ", field: "{{Ngày trả}}" },
      { label: "Nhà cung cấp / Khách hàng: ", field: "{{Nhà cung cấp}}" },
      { label: "Kho: ", field: "{{Kho}}" },
      { label: "Lý do trả: ", field: "{{Lý do trả}}", fullWidth: true },
      { label: "Số hoá đơn gốc: ", field: "{{Số hoá đơn}}" },
      { label: "Người nhận: ", field: "{{Người nhận}}" },
      { label: "Ghi chú: ", field: "{{Ghi chú}}", fullWidth: true },
    ],
    detailColumns: [
      { header: "STT", field: "{{STT}}", width: 600, align: "center" },
      { header: "Mã hàng", field: "{{Mã hàng}}", width: 1300, align: "center" },
      { header: "Tên hàng", field: "{{Tên hàng}}", width: 3200, align: "left" },
      { header: "ĐVT", field: "{{ĐVT}}", width: 800, align: "center" },
      { header: "Số lượng trả", field: "{{Số lượng}}", width: 1200, align: "right", sum: true },
      { header: "Đơn giá", field: "{{Đơn giá}}", width: 1200, align: "right" },
      { header: "Thành tiền", field: "{{Thành tiền}}", width: 1400, align: "right", sum: true },
    ],
    signatureRoles: [
      { label: "NGƯỜI LẬP PHIẾU" },
      { label: "THỦ KHO" },
      { label: "NGƯỜI NHẬN HÀNG" },
    ],
  },
];

/** Look up a preset by id. */
export function getPresetTemplate(id: string): PresetTemplateConfig | undefined {
  return PRESET_TEMPLATES.find((p) => p.id === id);
}
